'use client';

import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback } from "react";
import { FormControl } from "../FormControl/FormControl.js";
import { useId } from "../../utils/hooks/useId.js";
import { useLiveAnnouncement } from "../../utils/hooks/useLiveAnnouncement.js";
import { classNames } from "../../utils/styles/classNames.js";
import { getFakeInputBorderClassName } from "./utils/getFakeInputBorderClassName.js";
import { handleAriaDescribedBy } from "./utils/handleAriaDescribedBy.js";
import { handleInputType } from "./utils/handleInputType.js";
import { parseProps } from "./utils/parseProps.js";
import { renderPrefix } from "./utils/renderPrefix.js";
import { renderSuffix } from "./utils/renderSuffix.js";
import { errorAnnouncement } from "./TextField.data.js";
import { defaultProps } from "./TextField.defaultProps.js";
import { input, fakeInput, inputSize, inputSpacing, wrapper } from "./TextField.css.js";
function TextFieldComponent(props, ref) {
  const { extraContentProps, formControlProps, inputProps } = parseProps(props);
  const {
    dataQa,
    id: inputId,
    inputSpacing: inputSpacingProp,
    inputSize: inputSizeProp,
    onBlur,
    ...restInputProps
  } = inputProps;
  const autoId = useId();
  const isAutoIdNeeded = !inputId || inputId === "";
  const id = isAutoIdNeeded ? autoId : inputId;
  const descriptionId = `chalk-textfield-description-id-${id}`;
  const inputSpacing$1 = inputSpacingProp || defaultProps.inputSpacing;
  const inputSize$1 = inputSizeProp || defaultProps.inputSize;
  const announce = useLiveAnnouncement();
  const handleBlur = useCallback(
    (event) => {
      if (formControlProps.error) {
        announce(
          errorAnnouncement(
            formControlProps.a11yLabel || formControlProps.label.children,
            formControlProps.error
          ),
          {
            id: `${id}-error`,
            a11yImportance: "polite"
          }
        );
      }
      onBlur == null ? void 0 : onBlur(event);
    },
    [formControlProps.error, onBlur]
  );
  return /* @__PURE__ */ jsx(
    FormControl,
    {
      ...formControlProps,
      descriptionId,
      label: {
        ...formControlProps.label,
        htmlFor: id
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: classNames(
            wrapper,
            inputSpacing[inputSpacing$1],
            inputSize[inputSize$1]
          ),
          children: [
            renderPrefix(extraContentProps, formControlProps.disabled),
            /* @__PURE__ */ jsx(
              "input",
              {
                ...restInputProps,
                ...handleAriaDescribedBy(
                  formControlProps,
                  descriptionId,
                  inputProps
                ),
                ...handleInputType(inputProps.type),
                "aria-invalid": formControlProps.error ? "true" : void 0,
                "aria-required": formControlProps.required ? "true" : void 0,
                className: input,
                "data-qa": dataQa,
                disabled: formControlProps.disabled,
                id,
                ref,
                onBlur: handleBlur
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: classNames(
                  fakeInput,
                  getFakeInputBorderClassName(formControlProps)
                )
              }
            ),
            renderSuffix(extraContentProps, formControlProps.disabled)
          ]
        }
      )
    }
  );
}
const TextField = /* @__PURE__ */ forwardRef(TextFieldComponent);
export {
  TextField
};
